import { avatarSrc } from "@/lib/defaults";
import { renderMentions, mentionQuery, MENTION_TRIGGER } from "@/components/MentionText";
import { renderRichText, renderLinks } from "@/components/RichText";
import { LinkCard, useLinkPreview } from "@/components/LinkCard";
import { parseFirstUrl } from "@/lib/embeds";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { isOffline } from "@/lib/netStatus";
import { EmojiTabsGrid } from "@/components/EmojiTabsGrid";
import { enqueueOffline, shouldQueue, EVT_FLUSHED, EVT_FAILED } from "@/lib/offlineQueue";
import { usePostChannel, sendPost } from "@/lib/postChannel";
import { PostLiveReactions } from "@/components/PostLiveReactions";
import { useApp } from "@/context/AppContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { GroupSharesLine } from "@/components/GroupSharesLine";
import { resolveMediaUrl } from "@/components/FileUpload";
import { PostAudioPlayer } from "@/components/PostAudioPlayer";
import { localizeReference } from "@/lib/bibleBooks";
import { useBackHandler } from "@/lib/backButton";
import { emitPostPatch } from "@/lib/postSync";
import { Send, Trash2, X, Loader2, MessageCircle, Smile, Pin, Star, Flag, ChevronLeft, ChevronRight, Megaphone, Maximize2, WifiOff } from "lucide-react";
import { enterVideoFullscreen } from "@/lib/videoFullscreen";
import { toast } from "sonner";
import { ReportDialog } from "@/components/ReportDialog";
import { AdCtaFooter } from "@/components/AdCtaFooter";
import { AdStageCta } from "@/components/AdStageCta";
import { PostPhotos, Lightbox, LightboxAuthor, photoCaptionChars } from "@/components/PhotoGallery";
import { postBeforeAfter } from "@/lib/postMedia";
import { BeforeAfterBlock } from "@/components/post/BeforeAfter";
import { AlbumMosaic } from "@/components/post/AlbumMosaic";
import { ZoomableImage } from "@/components/ZoomableImage";
import { MediaReactionBar } from "@/components/MediaReactionBar";
import { EditableCaption } from "@/components/EditableCaption";
import { VideoPlayer } from "@/components/VideoPlayer";
import { useIsMobile } from "@/lib/useIsMobile";
import { useUserBlocked, withoutAuthor } from "@/lib/blocks";
import { BlockUserDialog } from "@/components/BlockUserDialog";
import { EmbedPlayer } from "@/components/EmbedPlayer";
import { parseVideoEmbed } from "@/lib/embeds";
import { stripEmbedUrl } from "@/lib/linkText";
import { bgStyle } from "@/lib/gradients";
import { AutoFitText } from "@/components/AutoFitText";
import { BgTextFrame, BG_TEXT_PAD, BG_TEXT_SHADOW, BG_TEXT_MAX_PX } from "@/components/BgTextFrame";
import { ReactionBar } from "@/components/ReactionBar";
import { AttnPulse } from "@/components/AttnPulse";
import { ReactorsDialog } from "@/components/ReactorsDialog";
import { timeAgo } from "@/lib/timeAgo";
import { keepFocusTap } from "@/lib/keepFocusTap";
import { GuestCta } from "@/components/GuestCta";
import { requireLogin } from "@/lib/guestGate";
import { profileGateProps } from "@/lib/profileDone";

const EMOJI = { love: "👍", adore: "❤️", amen: "🙏", gloire: "🙌", sad: "😢", rire: "😂", wow: "😮" };

// Lot 667 : liens cliquables (libellé court) + carte d'aperçu riche sous le commentaire.
const renderContent = (content, mentions) => renderLinks(String(content || ""), (leaf) => renderMentions(leaf, mentions), "c");

// What goes on the big screen of the theater layout (video / replay posts only).
// Album mêlant vidéos → visionneuse « media » : un média à la fois (vidéo avec le son ou photo), ◀ ▶ entre tous.
const theaterMediaOf = (post) => {
  const album = (Array.isArray(post.album) && post.album.length > 1 && post.album.some((m) => m.type === "video")) ? post.album : null;
  if (album) return { kind: "media", items: album };
  if (post.video_url) return { kind: "video", src: post.hls_url || post.video_url, poster: post.poster_url || undefined, processing: !!post.video_processing };
  const embed = (post.embed && post.embed.src) ? post.embed : parseVideoEmbed(post.content || "");
  if (embed?.src && !post.images?.length) return { kind: "embed", embed };
  return null;
};

// Theater kind decided from props only (shared by the Dialog shell and the keyed body).
const theaterKindFor = (post, { initialPhoto = null, isMobile = false, embedded = false } = {}) => {
  if (embedded || isMobile) return null;
  const base = theaterMediaOf(post);
  if (base) return base.kind;
  const hasPhotos = (post.images?.length > 0) || (Array.isArray(post.album) && post.album.some((m) => m.type !== "video"));
  return initialPhoto !== null && hasPhotos ? "photos" : null;
};

// PC theater: the media on a black stage (left), the post + comments column on the right.
// kind "photos" (Facebook photo viewer) / "media" (album photos + vidéos) : counter, ◀ ▶, pinch/wheel zoom, ← → keys ;
// une vidéo de l'album est lue avec le son (lecteur à contrôles).
const TheaterStage = ({ post, media, mediaCtl, onOpenPhoto, photoIndex = 0, onPhotoIndex, videoNav = null }) => {
  const { t } = useApp();
  const stageRef = useRef(null);
  const slides = media.kind === "photos" ? media.images.map((u) => ({ type: "image", url: u })) : media.kind === "media" ? media.items : [];
  const isSlides = slides.length > 0;
  const cur = slides[Math.min(photoIndex, Math.max(0, slides.length - 1))] || null;
  const go = useCallback((d) => onPhotoIndex?.((i) => (i + d + slides.length) % slides.length), [onPhotoIndex, slides.length]);
  useEffect(() => {
    if (!isSlides || slides.length < 2) return undefined;
    const onKey = (e) => {
      if (e.target && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "ArrowRight") go(1); else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isSlides, slides.length, go]);
  // Video / replay: ← → step through the videos of the feed (same keys as the photo viewer).
  const nav = media.kind === "video" ? videoNav : null;
  useEffect(() => {
    if (!nav) return undefined;
    const onKey = (e) => {
      if (e.target && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (e.key === "ArrowRight" && nav.hasNext) nav.next();
      else if (e.key === "ArrowLeft" && nav.hasPrev) nav.prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav]);
  // ⛶ sur tous les appareils (PC = plein écran navigateur ; téléphone = vrai plein écran, paysage si vidéo horizontale).
  const fsButton = media.kind === "video";
  return (
    <div className="flex-1 min-w-0 min-h-0 flex flex-col bg-black" data-testid={`theater-stage-${post.id}`}>
    {/* overflow-hidden: the blurred poster backdrop (scale 1.15 + blur 28px) must never bleed under the comments column. */}
    <div ref={stageRef} className="flex-1 min-h-0 relative flex items-center justify-center overflow-hidden">
      {isSlides && cur && (
        <>
          <div className="absolute top-4 left-4 z-10 text-white/80 text-sm font-semibold" style={{ top: "calc(var(--sa-top, 0px) + 1rem)" }} data-testid="theater-photo-counter">{photoIndex + 1} / {slides.length}</div>
          {slides.length > 1 && (
            <>
              <button onClick={() => go(-1)} className="absolute left-4 z-10 h-11 w-11 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors" data-testid="theater-photo-prev"><ChevronLeft size={26} /></button>
              <button onClick={() => go(1)} className="absolute right-4 z-10 h-11 w-11 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors" data-testid="theater-photo-next"><ChevronRight size={26} /></button>
            </>
          )}
          <div className="w-full h-full p-6 flex items-center justify-center" style={{ paddingTop: "calc(var(--sa-top, 0px) + 1.5rem)", paddingBottom: mediaCtl ? "6rem" : undefined }}>
            {cur.type === "video" ? (
              <VideoPlayer key={`v-${photoIndex}-${cur.url}`} mode="controls" src={cur.url} poster={cur.poster_url || undefined} width={cur.width || 0} height={cur.height || 0} fx={cur.fx || null} autoPlay adaptRatio maxHeight="100%"
                className="w-full max-w-[1100px] rounded-lg" testId={`theater-media-video-${post.id}`} speedControl fullscreenButton />
            ) : (
              <ZoomableImage key={cur.url} src={resolveMediaUrl(cur.url)} testId="theater-photo" onSwipe={slides.length > 1 ? go : undefined} />
            )}
          </div>
          {mediaCtl && (
            <div className="absolute left-1/2 -translate-x-1/2 z-10 w-[min(92%,560px)] flex flex-col items-center gap-2" style={{ bottom: "calc(var(--sa-bottom, 0px) + 1rem)" }} data-testid="theater-photo-footer">
              {(post.album?.[photoIndex]?.caption || mediaCtl.canEditCaption) && (
                <div className="px-3 py-1.5 rounded-lg bg-black/50 backdrop-blur w-full" data-testid="theater-photo-caption">
                  <EditableCaption postId={post.id} mediaIndex={photoIndex} caption={post.album?.[photoIndex]?.caption || ""} canEdit={mediaCtl.canEditCaption} dark onSaved={(c) => mediaCtl.updateCaption(photoIndex, c)} testId="theater-photo-caption-editor" />
                </div>
              )}
              <div className="rounded-2xl bg-black/50 backdrop-blur px-1.5 border border-white/15">
                <MediaReactionBar key={photoIndex} postId={post.id} mediaIndex={photoIndex} initial={mediaCtl.mrFor(photoIndex)} dark onChange={(c, my) => mediaCtl.onMediaReact(photoIndex, c, my)} testId="theater-photo-reactions" />
              </div>
            </div>
          )}
        </>
      )}
      {media.kind === "video" && (
        <>
          {media.poster && (
            <div className="absolute inset-0 pointer-events-none" aria-hidden
              style={{ backgroundImage: `url(${resolveMediaUrl(media.poster)})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(28px)", transform: "scale(1.15)", opacity: 0.45 }} />
          )}
          <VideoPlayer mode="controls" src={media.src} poster={media.poster} fx={post.video_fx || null} autoPlay initialProcessing={media.processing} className="relative z-[1] w-full h-full" testId={`theater-video-${post.id}`} resumeKey={post.id} resumeAt={post.resume_t || 0} speedControl />
          {nav && (nav.hasPrev || nav.hasNext) && (
            <>
              <button type="button" onClick={nav.prev} disabled={!nav.hasPrev} aria-label={t("theater.prev_video", "Vidéo précédente")}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-10 h-11 w-11 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-25 disabled:cursor-default"
                data-testid="theater-video-prev"><ChevronLeft size={26} /></button>
              <button type="button" onClick={nav.next} disabled={!nav.hasNext} aria-label={t("theater.next_video", "Vidéo suivante")}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-10 h-11 w-11 grid place-items-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-25 disabled:cursor-default"
                data-testid="theater-video-next">{nav.loading ? <Loader2 size={22} className="animate-spin" /> : <ChevronRight size={26} />}</button>
            </>
          )}
          {(post.is_replay || fsButton) && (
            <div className="absolute left-4 z-10 flex items-center gap-2" style={{ top: "calc(var(--sa-top, 0px) + 1rem)" }} data-testid={`theater-video-topbar-${post.id}`}>
              {fsButton && (
                <button type="button" onClick={() => enterVideoFullscreen(stageRef.current?.querySelector("video"))}
                  className="h-9 w-9 rounded-full grid place-items-center text-white transition-transform hover:scale-110"
                  style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
                  aria-label={t("video.fullscreen", "Plein écran")} data-testid={`theater-fullscreen-${post.id}`}>
                  <Maximize2 size={16} />
                </button>
              )}
              {post.is_replay && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide text-white pointer-events-none"
                  style={{ background: "rgba(217,87,87,0.94)", backdropFilter: "blur(4px)" }} data-testid={`theater-replay-badge-${post.id}`}>
                  {t("post.replay_badge", "Replay")}
                </div>
              )}
            </div>
          )}
        </>
      )}
      {media.kind === "embed" && (
        <div className="w-full max-w-[1100px] aspect-video max-h-full px-4" data-testid={`theater-embed-${post.id}`}>
          <EmbedPlayer embed={media.embed} preview={false} sound testId={`comment-post-embed-${post.id}`} />
        </div>
      )}
    </div>
    {/* Sponsored post: the advertiser's CTA stays visible under the media (Facebook video-ad style). */}
    {post.ad && <AdStageCta ad={post.ad} author={post.author} className="shrink-0" />}
    </div>
  );
};

const PostFull = ({ post, onComment, onOpenPhoto, media, hideMedia = false, focusReactions = false }) => {
  const { t, lang } = useApp();
  const author = post.author || {};
  const albumMedia = (Array.isArray(post.album) && post.album.length > 1 && post.album.some((m) => m.type === "video")) ? post.album : null;
  const content = post.content || "";
  const storedEmbed = (post.embed && (post.embed.src || post.embed.kind === "article")) ? post.embed : parseVideoEmbed(content);
  // Lot 667 : sans carte enregistrée, l'aperçu du lien (Facebook, article…) est récupéré à l'affichage.
  const lazyEmbed = useLinkPreview((!storedEmbed && !post.embed_dismissed && !post.images?.length && !post.video_url && !post.verse) ? parseFirstUrl(content) : null);
  const embed = storedEmbed || (lazyEmbed?.kind === "article" ? lazyEmbed : null);
  // Carte de lien affichée sous le texte → l'URL brute n'est pas répétée.
  const shownText = (!hideMedia && !post.images?.length && !post.video_url && embed) ? stripEmbedUrl(content, embed) : content;
  return (
    <div className="px-4 pt-3" data-testid={`comment-post-full-${post.id}`}>
      {(post.ad || post.is_ad || post.is_promoted) && (
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--yalola-text-muted)" }} data-testid={`comment-post-sponsored-${post.id}`}>
          <Megaphone size={12} /> {t("post.sponsored", "Sponsorisé")}
        </div>
      )}
      <header className="flex items-start gap-3">
        <Link to={author.is_church && author.church_id ? `/churches/${author.church_id}` : `/profile/${author.id}`}>
          <Avatar className="h-10 w-10"><AvatarImage src={avatarSrc(author)} /><AvatarFallback>{author.name?.[0]}</AvatarFallback></Avatar>
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link to={author.is_church && author.church_id ? `/churches/${author.church_id}` : `/profile/${author.id}`} className="font-semibold text-sm hover:underline">{author.name}</Link>
            <VerifiedBadge user={author} size={13} />
            {post.is_replay && (
              <span className="chip text-[10px]" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}>{t("post.replay_alt", "Rediffusion")}</span>
            )}
          </div>
          <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{timeAgo(post.created_at, t)}{post.edited_at ? ` · ${t("post.edited", "modifié")}` : ""}</div>
          <GroupSharesLine post={post} />
        </div>
      </header>

      {shownText && post.bg && bgStyle(post.bg) && !post.images?.length && !post.video_url && !post.audio_url ? (
        <BgTextFrame bg={post.bg} className="mt-3" data-testid={`comment-post-bg-${post.id}`}>
          <AutoFitText text={shownText} scale={post.text_scale || 1} maxPx={BG_TEXT_MAX_PX} boxClassName={BG_TEXT_PAD} className="font-bold text-white" style={BG_TEXT_SHADOW} />
        </BgTextFrame>
      ) : shownText ? (
        <div className="mt-3 text-[15px] leading-relaxed break-words" data-testid={`post-text-${post.id}`}>{renderRichText(shownText, (leaf) => renderMentions(leaf, post.mentions))}</div>
      ) : null}

      {post.verse && (
        <div
          className="mt-3 rounded-2xl p-6 relative overflow-hidden"
          style={post.verse.bg && post.verse.bg.startsWith("/verse-bg/")
            ? { backgroundImage: `url(${post.verse.bg})`, backgroundSize: "cover", backgroundPosition: "center" }
            : { background: "var(--gradient-navy)" }}
          data-testid={`comment-post-verse-${post.id}`}
        >
          {post.verse.bg && post.verse.bg.startsWith("/verse-bg/") && <div className="absolute inset-0 bg-black/35" aria-hidden />}
          <div className="relative">
            <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "var(--yalola-accent)" }}>{t("verse.title", "Verset du jour")}</div>
            <p className="serif-italic mt-2 text-lg leading-relaxed text-white/95" style={{ textShadow: "0 1px 3px rgba(0,0,0,0.35)" }}>« {post.verse[`text_${lang}`] || post.verse.text_fr || post.verse.text} »</p>
            <div className="mt-3 text-sm font-semibold" style={{ color: "var(--yalola-accent)" }}>— {localizeReference(post.verse.reference, lang)}</div>
          </div>
        </div>
      )}

      {!hideMedia && albumMedia && (
        postBeforeAfter(post)
          ? <BeforeAfterBlock post={post} ba={postBeforeAfter(post)} onOpen={(i) => onOpenPhoto?.(i)} />
          : <AlbumMosaic post={post} items={albumMedia} onOpen={(i) => onOpenPhoto?.(i)} />
      )}
      {!hideMedia && !albumMedia && post.images?.length > 0 && (
        postBeforeAfter(post)?.kind === "image"
          ? <BeforeAfterBlock post={post} ba={postBeforeAfter(post)} onOpen={(i) => onOpenPhoto?.(i)} />
          : <PostPhotos images={post.images} onOpen={(i) => onOpenPhoto?.(i)} />
      )}
      {!hideMedia && !albumMedia && post.video_url && (
        <div className="relative mt-3">
          <VideoPlayer mode="controls" src={post.hls_url || post.video_url} poster={post.poster_url || undefined} width={post.video_width || 0} height={post.video_height || 0} fx={post.video_fx || null} autoPlay adaptRatio initialProcessing={!!post.video_processing}
            className="rounded-xl border" testId={`comment-post-video-${post.id}`} resumeKey={post.id} resumeAt={post.resume_t || 0} />
          {post.is_replay && (
            <div className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide text-white pointer-events-none"
              style={{ background: "rgba(217,87,87,0.94)", backdropFilter: "blur(4px)" }} data-testid={`comment-post-video-${post.id}-replay-badge`}>
              {t("post.replay_badge", "Replay")}
            </div>
          )}
        </div>
      )}
      {!hideMedia && !post.images?.length && !post.video_url && embed && (
        embed.kind === "article" ? (
          <a href={embed.url} target="_blank" rel="noreferrer" className="mt-3 block rounded-2xl overflow-hidden border" style={{ borderColor: "var(--yalola-border)" }} data-testid={`comment-post-article-${post.id}`}>
            {embed.thumbnail && <img src={resolveMediaUrl(embed.thumbnail)} loading="lazy" decoding="async" alt="" className="w-full max-h-[360px] object-cover" />}
            <div className="px-4 py-3" style={{ background: "var(--yalola-surface-alt)" }}>
              <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{embed.domain}</div>
              {embed.title && <div className="text-sm font-bold leading-snug">{embed.title}</div>}
              {embed.description && <div className="text-xs mt-0.5 line-clamp-2" style={{ color: "var(--yalola-text-muted)" }}>{embed.description}</div>}
            </div>
          </a>
        ) : (
          <div className="mt-1">
            <EmbedPlayer embed={embed} testId={`comment-post-embed-${post.id}`} />
            {(post.embed?.title || post.embed?.author) && (
              <div className="mt-1 rounded-xl border px-3 py-2" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface-alt)" }}>
                <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}>{embed.provider === "youtube" ? "YOUTUBE.COM" : "FACEBOOK.COM"}</div>
                {post.embed?.title && <div className="text-sm font-semibold truncate">{post.embed.title}</div>}
                {post.embed?.author && <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>{post.embed.author}</div>}
              </div>
            )}
          </div>
        )
      )}
      {post.audio_url && (
        <div className="mt-3" data-testid={`comment-post-audio-${post.id}`}>
          <PostAudioPlayer post={post} author={author} testId={`comment-post-audio-player-${post.id}`} />
        </div>
      )}

      {post.ad && <AdCtaFooter ad={post.ad} author={author} />}

      {/* Facebook-style reaction/comment/share bar inside the modal */}
      <AttnPulse active={focusReactions} testId={`comment-post-reactions-${post.id}`}>
        <ReactionBar post={post} commentCount={post.comment_count} onCommentClick={onComment} autoOpenReactors={focusReactions} />
      </AttnPulse>
    </div>
  );
};

const reactTotal = (c) => Object.values(c?.reactions || {}).reduce((a, b) => a + (b || 0), 0);

const CommentRow = ({ c, isReply, replyCount = 0, expanded = false, t, lang, user, reactions, onReact, onReply, onEdit, onDelete, onToggleReplies, onPin, onReport, onBlock, isPostOwner, isBest, onOpenReactors, pulseId = null }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(c.content);
  const rl = (r) => (r ? r[`label_${lang}`] || r.label_fr || r.key : "");
  const current = reactions.find((r) => r.key === c.my_reaction);
  const total = Object.values(c.reactions || {}).reduce((a, b) => a + b, 0);
  const topEmojis = reactions.filter((r) => (c.reactions?.[r.key] || 0) > 0).slice(0, 3);
  const canEdit = !c.pending && user && user.id === c.author?.id;
  const canDelete = !c.pending && user && (user.id === c.author?.id || user.role === "super_admin" || user.role === "moderator");
  const pick = (key) => { setPickerOpen(false); onReact(c, key); };
  const saveEdit = () => { const v = editText.trim(); if (!v) return; onEdit(c, v); setEditing(false); };

  return (
    <div className={`flex gap-2 items-start ${isReply ? "ml-10 mt-3" : ""} ${pulseId === c.id ? "yalola-attn-pulse" : ""} ${c.pending ? "opacity-60" : ""}`} data-comment-id={c.id} data-pending={c.pending ? "1" : undefined} data-testid={`comment-row-${c.id}`}>
      <Link to={`/profile/${c.author?.id}`}>
        <Avatar className={isReply ? "h-7 w-7" : "h-8 w-8"}>
          <AvatarImage src={avatarSrc(c.author)} />
          <AvatarFallback>{c.author?.name?.[0]}</AvatarFallback>
        </Avatar>
      </Link>
      <div className="min-w-0 flex-1">
        {c.pinned && (
          <div className="flex items-center gap-1 mb-1 ml-1 text-[11px] font-bold uppercase tracking-wide" style={{ color: "var(--yalola-primary)" }} data-testid={`comment-pinned-badge-${c.id}`}>
            <Pin size={11} className="fill-current" /> {t("comment.pinned", "Épinglé par l'auteur")}
          </div>
        )}
        {isBest && (
          <div className="flex items-center gap-1 mb-1 ml-1 text-[11px] font-bold" style={{ color: "#E0A63C" }} data-testid={`comment-best-reply-${c.id}`}>
            <Star size={11} className="fill-current" /> {t("comment.best_reply", "Meilleure réponse")}
          </div>
        )}

        {editing ? (
          <div className="flex items-center gap-2 mb-1">
            <input
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEdit(); } if (e.key === "Escape") { setEditing(false); setEditText(c.content); } }}
              autoFocus
              className="flex-1 rounded-full border px-3 h-9 text-sm bg-white outline-none"
              style={{ borderColor: "var(--yalola-border)" }}
              data-testid={`comment-edit-input-${c.id}`}
            />
            <button onClick={saveEdit} className="text-xs font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid={`comment-edit-save-${c.id}`}>{t("common.save", "Enregistrer")}</button>
            <button onClick={() => { setEditing(false); setEditText(c.content); }} className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid={`comment-edit-cancel-${c.id}`}>{t("common.cancel", "Annuler")}</button>
          </div>
        ) : (
          <div className="inline-block max-w-full mb-1">
            <div className="rounded-2xl px-3 py-2" style={{ background: "var(--yalola-surface-alt)" }}>
              <div className="flex items-center gap-1.5 flex-wrap">
                <Link to={`/profile/${c.author?.id}`} className="text-xs font-semibold hover:underline inline-flex items-center gap-1">
                  {c.author?.name} <VerifiedBadge user={c.author} size={11} />
                </Link>
                <span className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid={`comment-time-${c.id}`}>
                  {timeAgo(c.created_at, t)}{c.edited_at ? ` · ${t("comment.edited", "modifié")}` : ""}
                </span>
                {c.offline && (
                  <span className="text-[11px] inline-flex items-center gap-1 font-medium" style={{ color: "#B45309" }} data-testid={`comment-offline-${c.id}`}>
                    <WifiOff size={11} /> {t("offline.waiting", "En attente du réseau")}
                  </span>
                )}
              </div>
              <div className="text-sm whitespace-pre-wrap break-words">{renderContent(c.content, c.mentions)}</div>
              {parseFirstUrl(c.content) && <LinkCard url={parseFirstUrl(c.content)} testId={`comment-link-${c.id}`} compact />}
            </div>
          </div>
        )}

        {!editing && (
          <div className="flex items-center gap-4 px-1 text-[12px] flex-wrap" style={{ color: "var(--yalola-text-muted)" }}>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <button
                  className="font-semibold hover:underline"
                  style={{ color: current ? current.color : "var(--yalola-text-muted)" }}
                  onMouseEnter={() => setPickerOpen(true)}
                  data-testid={`comment-react-btn-${c.id}`}
                >
                  {current ? `${EMOJI[current.key] || "👍"} ${rl(current)}` : t("comment.like", "J'aime")}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-1.5 rounded-full flex items-center gap-1 z-[110]" align="start" side="top" data-testid={`comment-react-picker-${c.id}`} onMouseLeave={() => setPickerOpen(false)}>
                {reactions.map((r) => (
                  <button
                    key={r.key}
                    onClick={() => pick(r.key)}
                    title={rl(r)}
                    className="text-[26px] leading-none transition-transform hover:scale-125 hover:-translate-y-0.5 rounded-full"
                    style={{ background: c.my_reaction === r.key ? "var(--yalola-primary-soft)" : "transparent", padding: 2 }}
                    data-testid={`comment-react-${r.key}-${c.id}`}
                  >
                    {EMOJI[r.key] || "👍"}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
            <button className="font-semibold hover:underline" onClick={() => onReply(c)} data-testid={`comment-reply-btn-${c.id}`}>
              {t("comment.reply", "Répondre")}
            </button>
            {canEdit && (
              <button className="font-semibold hover:underline" onClick={() => { setEditing(true); setEditText(c.content); }} data-testid={`comment-edit-btn-${c.id}`}>
                {t("comment.edit", "Modifier")}
              </button>
            )}
            {user && user.id !== c.author?.id && (
              <button className="font-semibold hover:underline text-red-600" onClick={() => onReport(c)} data-testid={`comment-report-btn-${c.id}`}>
                {t("post.report", "Signaler")}
              </button>
            )}
            {user && user.id !== c.author?.id && c.author?.id && (
              <button className="font-semibold hover:underline text-red-600" onClick={() => onBlock(c.author)} data-testid={`comment-block-btn-${c.id}`}>
                {t("block.confirm", "Bloquer")}
              </button>
            )}
            <div className="ml-auto flex items-center gap-3">
              {total > 0 && (
                <button
                  type="button"
                  onClick={() => onOpenReactors?.(c.id)}
                  className="inline-flex items-center gap-0.5 bg-white rounded-full shadow-sm border px-1.5 h-[22px] text-[11px] font-medium hover:brightness-95 transition"
                  style={{ borderColor: "var(--yalola-border)" }}
                  data-testid={`comment-reactions-${c.id}`}
                  title={t("reactions.who_title", "Qui a réagi")}
                >
                  {topEmojis.map((r) => <span key={r.key} className="leading-none text-[13px]">{EMOJI[r.key] || "👍"}</span>)}
                  <span style={{ color: "var(--yalola-text-muted)" }}>{total}</span>
                </button>
              )}
              {isPostOwner && !isReply && (
                <button
                  className="hover:underline font-semibold inline-flex items-center gap-0.5"
                  style={{ color: c.pinned ? "var(--yalola-primary)" : "var(--yalola-text-muted)" }}
                  onClick={() => onPin(c)}
                  data-testid={`comment-pin-btn-${c.id}`}
                  title={c.pinned ? t("comment.unpin", "Désépingler") : t("comment.pin", "Épingler")}
                >
                  <Pin size={12} className={c.pinned ? "fill-current" : ""} />
                </button>
              )}
              {canDelete && (
                <button className="hover:text-red-600" onClick={() => onDelete(c)} data-testid={`comment-delete-btn-${c.id}`} title={t("comment.delete", "Supprimer")}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          </div>
        )}

        {!isReply && replyCount > 0 && (
          <button className="mt-1.5 ml-1 text-[12px] font-semibold hover:underline" style={{ color: "var(--yalola-primary)" }} onClick={() => onToggleReplies(c.id)} data-testid={`comment-toggle-replies-${c.id}`}>
            {expanded ? t("comment.hide_replies", "Masquer les réponses") : t("comment.view_replies", "Voir {count} réponse(s)").replace("{count}", replyCount)}
          </button>
        )}
      </div>
    </div>
  );
};

const CommentModalBody = ({ post, open, onOpenChange, onCountChange, focusCommentId = null, focusReactions = false, initialPhoto = null, onPhotoClose, embedded = false, guardRef = null, videoNav = null }) => {
  const { user, t, lang, config } = useApp();
  const isMobile = useIsMobile();
  // PC photo viewer (Facebook-style theater): opened by a photo tap (`initialPhoto`) on a photo-only post
  // → the photo goes on the black stage, this column shows the post + comments; no lightbox on top.
  const baseTheater = useMemo(() => theaterMediaOf(post), [post]);
  const hasPhotos = (post.images?.length > 0) || (Array.isArray(post.album) && post.album.some((m) => m.type !== "video"));
  const photoTheater = !embedded && !isMobile && initialPhoto !== null && !baseTheater && hasPhotos;
  // In-place photo zoom (fullscreen lightbox OVER this same screen — no separate theater).
  // `initialPhoto` indexes post.album when the post is an album, else post.images.
  const [lightbox, setLightbox] = useState(photoTheater || baseTheater?.kind === "media" ? null : initialPhoto);
  const [postMeta, setPostMeta] = useState({ album: post.album, images: post.images });
  const zoomImages = useMemo(() => {
    const album = Array.isArray(postMeta.album) && postMeta.album.length > 1 ? postMeta.album : null;
    return album ? album.filter((m) => m.type !== "video").map((m) => m.url) : (postMeta.images || []);
  }, [postMeta]);
  const albumImageIndex = (i) => { // album index → position among the album's images
    const album = Array.isArray(postMeta.album) && postMeta.album.length > 1 ? postMeta.album : null;
    if (!album) return i;
    return Math.max(0, album.slice(0, i).filter((m) => m.type !== "video").length);
  };
  const [stageIndex, setStageIndex] = useState(() => {
    // Album avec vidéos (visionneuse « media ») : l'index est celui de l'album ; photos seules : position parmi les photos.
    if (baseTheater?.kind === "media") return Math.min(Math.max(0, initialPhoto || 0), Math.max(0, baseTheater.items.length - 1));
    return Math.min(Math.max(0, albumImageIndex(initialPhoto || 0)), Math.max(0, zoomImages.length - 1));
  });
  useEffect(() => { if (guardRef) guardRef.current = lightbox !== null; }, [lightbox, guardRef]);
  const openPhoto = (i) => { if (baseTheater?.kind === "media") { setStageIndex(i); return; } setLightbox(albumImageIndex(i)); };
  const closePhoto = () => { setLightbox(null); onPhotoClose?.(); };
  // Video / replay posts open as a theater on PC (big screen left, this column right).
  // `embedded` (TikTok-style Reels sheet) = comments only, no Dialog, no theater, no media replay.
  const theaterMedia = useMemo(() => {
    if (photoTheater) return { kind: "photos", images: zoomImages };
    return theaterMediaOf({ ...post, album: postMeta.album });
  }, [post, postMeta.album, photoTheater, zoomImages]);
  const theater = !!theaterMedia && !isMobile && !embedded;
  // Per-media reactions + editable captions (albums / multi-photo posts), formerly theater-only.
  const [mediaReactions, setMediaReactions] = useState({});
  const hasMediaReactions = (Array.isArray(post.album) && post.album.length > 1) || (post.images?.length > 1);
  useEffect(() => {
    if (!open || !hasMediaReactions) return undefined;
    let alive = true;
    api.get(`/posts/${post.id}/media-reactions`).then(({ data }) => { if (alive) setMediaReactions(data.reactions || {}); }).catch(() => {});
    return () => { alive = false; };
  }, [open, post.id, hasMediaReactions]);
  const media = hasMediaReactions ? {
    mrFor: (i) => mediaReactions[String(i)] || { counts: {}, total: 0, my: null },
    onMediaReact: (i, counts, my) => setMediaReactions((prev) => ({ ...prev, [String(i)]: { counts, total: Object.values(counts).reduce((a, b) => a + b, 0), my } })),
    canEditCaption: !!(post.is_owner || ["super_admin", "moderator"].includes(user?.role)),
    updateCaption: (index, cap) => setPostMeta((prev) => {
      let album = Array.isArray(prev.album) ? [...prev.album] : [];
      if (album.length === 0 && (prev.images || []).length) album = prev.images.map((u) => ({ type: "image", url: u }));
      if (album[index]) album[index] = { ...album[index], caption: cap };
      return { ...prev, album };
    }),
  } : null;
  // Album index of the photo currently zoomed (captions/reactions are keyed by album index).
  const zoomAlbumIndex = (() => {
    if (lightbox === null) return null;
    const album = Array.isArray(postMeta.album) && postMeta.album.length > 1 ? postMeta.album : null;
    if (!album) return lightbox;
    let seen = -1;
    for (let k = 0; k < album.length; k++) { if (album[k].type !== "video" && ++seen === lightbox) return k; }
    return lightbox;
  })();
  const reactions = config?.reactions || [];
  const [comments, setComments] = useState(null);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);
  const [reportComment, setReportComment] = useState(null);
  const [blockTarget, setBlockTarget] = useState(null);
  useUserBlocked(useCallback((uid) => setComments((arr) => (arr ? withoutAuthor(arr, uid) : arr)), []));
  const [reporting, setReporting] = useState(false);
  const [expandedReplies, setExpandedReplies] = useState({});
  const [reactorsCommentId, setReactorsCommentId] = useState(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const [mentions, setMentions] = useState([]);
  const [mentionResults, setMentionResults] = useState([]);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [pulseId, setPulseId] = useState(null);
  const focusedRef = useRef(false);
  const inputRef = useRef(null);
  const typingTimersRef = useRef({});
  const lastTypingRef = useRef(0);
  const stopTypingRef = useRef(null);
  const mentionReqRef = useRef(0);

  // Android back button closes the comments sheet instead of leaving the page/exiting.
  useBackHandler(open, () => { onOpenChange(false); return true; });

  useEffect(() => {
    if (!open) return;
    setComments(null); setReplyTo(null); setText(""); setMentions([]); setTypingUsers({});
    api.get(`/posts/${post.id}/comments`).then(({ data }) => setComments(data.comments || [])).catch(() => setComments([]));
  }, [open, post.id]);

  // Canal temps réel PARTAGÉ (lib/postChannel : un seul WebSocket par publication, aussi utilisé par la
  // visionneuse pour les réactions en direct) : commentaires, réactions de commentaires, suppressions, saisie.
  usePostChannel(post.id, (msg) => {
    if (msg.type === "comment") {
      setComments((cs) => (cs && cs.some((x) => x.id === msg.comment.id) ? cs : [...(cs || []), msg.comment]));
    } else if (msg.type === "comment_deleted") {
      setComments((cs) => (cs || []).filter((x) => !msg.ids.includes(x.id)));
    } else if (msg.type === "comment_reaction") {
      setComments((cs) => (cs || []).map((x) => (x.id === msg.comment_id ? { ...x, reactions: msg.reactions } : x)));
    } else if (msg.type === "comment_edited") {
      setComments((cs) => (cs || []).map((x) => (x.id === msg.comment_id ? { ...x, content: msg.content, edited_at: msg.edited_at } : x)));
    } else if (msg.type === "comment_pinned") {
      setComments((cs) => (cs || []).map((x) => ({ ...x, pinned: x.id === msg.comment_id })));
    } else if (msg.type === "typing" && msg.user && msg.user.id !== user?.id) {
      const { id, name } = msg.user;
      setTypingUsers((prev) => {
        const next = { ...prev };
        if (msg.typing) next[id] = name; else delete next[id];
        return next;
      });
      clearTimeout(typingTimersRef.current[id]);
      if (msg.typing) {
        typingTimersRef.current[id] = setTimeout(() => setTypingUsers((p) => { const n = { ...p }; delete n[id]; return n; }), 5000);
      }
    }
  }, open);

  const sendTyping = () => {
    const now = Date.now();
    if (now - lastTypingRef.current > 1500) { lastTypingRef.current = now; sendPost(post.id, { type: "typing", typing: true }); }
    clearTimeout(stopTypingRef.current);
    stopTypingRef.current = setTimeout(() => { sendPost(post.id, { type: "typing", typing: false }); lastTypingRef.current = 0; }, 2500);
  };
  const stopTyping = () => { clearTimeout(stopTypingRef.current); lastTypingRef.current = 0; sendPost(post.id, { type: "typing", typing: false }); };

  const { topLevel, repliesOf } = useMemo(() => {
    const list = comments || [];
    const top = list.filter((c) => !c.parent_id).sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
    const map = {};
    list.filter((c) => c.parent_id).forEach((c) => { (map[c.parent_id] = map[c.parent_id] || []).push(c); });
    return { topLevel: top, repliesOf: map };
  }, [comments]);

  const onChangeText = (val) => {
    setText(val);
    sendTyping();
    const q = mentionQuery(val, mentions);
    if (q) {
      const rid = ++mentionReqRef.current;
      api.get(`/search?q=${encodeURIComponent(q)}`).then(({ data }) => {
        if (rid !== mentionReqRef.current) return;
        setMentionResults((data.users || []).slice(0, 5));
        setMentionOpen(true);
      }).catch(() => {});
    } else { setMentionOpen(false); setMentionResults([]); }
  };

  const pickMention = (u) => {
    setText((prev) => prev.replace(MENTION_TRIGGER, `@${u.name} `));
    setMentions((prev) => (prev.some((x) => x.id === u.id) ? prev : [...prev, { id: u.id, name: u.name }]));
    setMentionOpen(false); setMentionResults([]);
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  // Lot 599 — envoi optimiste : le commentaire apparaît tout de suite (légèrement estompé), le champ se vide ; à la
  // réponse il est remplacé par la version serveur ; en cas d'erreur il disparaît et le texte revient dans le champ.
  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    stopTyping();
    const used = mentions.filter((m) => text.includes(`@${m.name}`));
    const draft = { text, replyTo, mentions };
    const tempId = `tmp-${Date.now()}`;
    const temp = { id: tempId, author: { id: user?.id, name: user?.name, avatar_url: user?.avatar_url, is_verified: user?.is_verified }, author_id: user?.id,
                   content: text, parent_id: replyTo?.id || null, created_at: new Date().toISOString(), reactions: {}, my_reaction: null, mentions: used, pending: true };
    setComments((cs) => [...(cs || []), temp]);
    if (replyTo) setExpandedReplies((e) => ({ ...e, [replyTo.id]: true }));
    setText(""); setReplyTo(null); setMentions([]); setMentionOpen(false);
    const body = { content: draft.text, parent_id: draft.replyTo?.id || null, mentions: used };
    // Lot 630 — hors ligne : la bulle reste (« En attente du réseau »), le commentaire est mis en file et repart au retour
    // du réseau (même client_id → jamais de doublon) ; la bulle est remplacée par la version serveur à l'envoi.
    const queue = () => {
      enqueueOffline({ kind: "comment", method: "post", url: `/posts/${post.id}/comments`, body, meta: { post_id: post.id, temp_id: tempId } });
      setComments((cs) => (cs || []).map((x) => (x.id === tempId ? { ...x, offline: true } : x)));
      onCountChange?.(1);
      emitPostPatch(post.id, { comment_delta: 1 });
      setSending(false);
    };
    if (isOffline()) { queue(); return; }
    try {
      const { data } = await api.post(`/posts/${post.id}/comments`, body, { timeout: 45000 });
      setComments((cs) => {
        const base = (cs || []).filter((x) => x.id !== tempId);
        return base.some((x) => x.id === data.comment.id) ? base : [...base, data.comment];
      });
      onCountChange?.(1);
      emitPostPatch(post.id, { comment_delta: 1 });
    } catch (e) {
      if (shouldQueue(e)) { queue(); return; }
      setComments((cs) => (cs || []).filter((x) => x.id !== tempId));
      setText(draft.text); setReplyTo(draft.replyTo); setMentions(draft.mentions);
      toast.error(e?.friendlyMessage || "Erreur");
    } finally { setSending(false); }
  };

  // Lot 630 — la file hors ligne a envoyé (ou vu refuser) un commentaire de CETTE publication → bulle remplacée / retirée.
  useEffect(() => {
    const onFlushed = (e) => {
      const { item, data } = e.detail || {};
      if (item?.kind !== "comment" || item?.meta?.post_id !== post.id || !data?.comment) return;
      setComments((cs) => {
        const base = (cs || []).filter((x) => x.id !== item.meta.temp_id);
        return base.some((x) => x.id === data.comment.id) ? base : [...base, data.comment];
      });
    };
    const onFailed = (e) => {
      const { item } = e.detail || {};
      if (item?.kind !== "comment" || item?.meta?.post_id !== post.id) return;
      setComments((cs) => (cs || []).filter((x) => x.id !== item.meta.temp_id));
      onCountChange?.(-1);
      emitPostPatch(post.id, { comment_delta: -1 });
    };
    window.addEventListener(EVT_FLUSHED, onFlushed);
    window.addEventListener(EVT_FAILED, onFailed);
    return () => { window.removeEventListener(EVT_FLUSHED, onFlushed); window.removeEventListener(EVT_FAILED, onFailed); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.id]);

  // Lot 599 — réaction sur un commentaire : bascule locale immédiate, puis synchro avec la réponse serveur.
  const react = async (c, key) => {
    if (!user) { requireLogin("react"); return; }
    if (c.pending) return;
    const prev = { my_reaction: c.my_reaction, reactions: c.reactions || {} };
    const next = { ...prev.reactions };
    if (prev.my_reaction === key) { next[key] = Math.max(0, (next[key] || 1) - 1); }
    else { next[key] = (next[key] || 0) + 1; if (prev.my_reaction) next[prev.my_reaction] = Math.max(0, (next[prev.my_reaction] || 1) - 1); }
    setComments((cs) => (cs || []).map((x) => (x.id === c.id ? { ...x, my_reaction: prev.my_reaction === key ? null : key, reactions: next } : x)));
    // Lot 630 — hors ligne : la bascule reste affichée, l'envoi part au retour du réseau (le serveur bascule à l'identique).
    const queue = () => enqueueOffline({ kind: "comment_reaction", key: `comment:${c.id}`, method: "post", url: `/comments/${c.id}/react`, body: { type: key }, meta: { comment_id: c.id } });
    if (isOffline()) { queue(); return; }
    try {
      const { data } = await api.post(`/comments/${c.id}/react`, { type: key });
      setComments((cs) => (cs || []).map((x) => (x.id === c.id ? { ...x, my_reaction: data.my_reaction, reactions: data.reactions } : x)));
    } catch (e) {
      if (shouldQueue(e)) { queue(); return; }
      setComments((cs) => (cs || []).map((x) => (x.id === c.id ? { ...x, ...prev } : x)));
      toast.error(e?.friendlyMessage || "Erreur");
    }
  };

  const editComment = async (c, content) => {
    try {
      const { data } = await api.patch(`/comments/${c.id}`, { content });
      setComments((cs) => (cs || []).map((x) => (x.id === c.id ? { ...x, content: data.content, edited_at: data.edited_at } : x)));
      toast.success(t("comment.edited_toast", "Commentaire modifié"));
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const del = async (c) => {    try {
      const { data } = await api.delete(`/comments/${c.id}`);
      setComments((cs) => (cs || []).filter((x) => x.id !== c.id && x.parent_id !== c.id));
      onCountChange?.(-(data.deleted || 1));
      emitPostPatch(post.id, { comment_delta: -(data.deleted || 1) });
      toast.success(t("comment.deleted", "Commentaire supprimé"));
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const toggleReplies = (id) => setExpandedReplies((e) => ({ ...e, [id]: !e[id] }));

  const isPostOwner = !!user && (user.id === post.author?.id || user.role === "super_admin" || user.role === "moderator");

  // Auto-expand the replies of a pinned comment so the best exchanges are surfaced.
  useEffect(() => {
    const pinned = (comments || []).find((c) => c.pinned && !c.parent_id);
    if (pinned && expandedReplies[pinned.id] === undefined && ((repliesOf[pinned.id] || []).length > 0)) {
      setExpandedReplies((e) => ({ ...e, [pinned.id]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [comments]);

  const pinComment = async (c) => {
    const willPin = !c.pinned;
    setComments((cs) => (cs || []).map((x) => ({ ...x, pinned: x.id === c.id ? willPin : false })));
    try {
      if (willPin) await api.post(`/posts/${post.id}/comments/${c.id}/pin`);
      else await api.delete(`/posts/${post.id}/comments/${c.id}/pin`);
      toast.success(willPin ? t("comment.pinned_toast", "Commentaire épinglé 📌") : t("comment.unpinned_toast", "Commentaire désépinglé"));
    } catch (e) {
      setComments((cs) => (cs || []).map((x) => ({ ...x, pinned: x.id === c.id ? !willPin : x.pinned })));
      toast.error(e?.friendlyMessage || "Erreur");
    }
  };

  const startReply = (c) => {
    if (!user) { requireLogin("comment"); return; }
    const targetId = c.parent_id || c.id;
    const nested = !!c.parent_id;
    setReplyTo({ id: targetId, name: c.author?.name, nested });
    if (nested && c.author?.id && c.author?.name) {
      setMentions((prev) => (prev.some((x) => x.id === c.author.id) ? prev : [...prev, { id: c.author.id, name: c.author.name }]));
      setText((prev) => (prev && prev.startsWith("@") ? prev : `@${c.author.name} `));
    }
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const insertEmoji = (e) => { setText((prev) => prev + e); setEmojiOpen(false); setTimeout(() => inputRef.current?.focus(), 30); };

  const rowProps = { t, lang, user, reactions, onReact: react, onReply: startReply, onEdit: editComment, onDelete: del, onToggleReplies: toggleReplies, onPin: pinComment, onReport: setReportComment, onBlock: setBlockTarget, isPostOwner, onOpenReactors: setReactorsCommentId, pulseId };

  // Opened from a notification (« X a répondu à votre commentaire ») : unfold the
  // thread that holds the target comment, scroll to it and pulse it once.
  useEffect(() => {
    if (!open || !focusCommentId || focusedRef.current || !comments) return;
    const target = comments.find((c) => c.id === focusCommentId);
    if (!target) { focusedRef.current = true; return; }
    if (target.parent_id) setExpandedReplies((prev) => ({ ...prev, [target.parent_id]: true }));
    const tm = setTimeout(() => {
      const el = document.querySelector(`[data-testid="comment-modal-${post.id}"] [data-comment-id="${focusCommentId}"], [data-testid="comment-sheet-${post.id}"] [data-comment-id="${focusCommentId}"]`);
      if (el) { el.scrollIntoView({ behavior: "smooth", block: "center" }); setPulseId(focusCommentId); setTimeout(() => setPulseId(null), 2200); }
      focusedRef.current = true;
    }, 250);
    return () => clearTimeout(tm);
  }, [open, focusCommentId, comments, post.id]);

  const doReportComment = async (reason, category) => {
    if (!reportComment || reporting) return;
    setReporting(true);
    try {
      await api.post(`/comments/${reportComment.id}/report`, { reason, category });
      toast.success(
        category === "child_safety"
          ? t("report.csae_toast", "Signalement urgent transmis à notre équipe de sécurité. Merci de protéger notre communauté.")
          : t("comment.reported_toast", "Merci, ce commentaire a été signalé aux modérateurs")
      );
      setReportComment(null);
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setReporting(false); }
  };
  const typingNames = Object.values(typingUsers);
  const typingLabel = typingNames.length === 1
    ? t("comment.typing_one", "{name} est en train d'écrire…").replace("{name}", typingNames[0])
    : t("comment.typing_many", "Plusieurs personnes écrivent…");

  const commentTotal = comments === null ? (post.comment_count || 0) : comments.length;
  const column = (
        <div className={`flex flex-col min-h-0 flex-1 ${theater ? "lg:flex-none lg:w-[420px] xl:w-[460px] border-l bg-background relative z-[2]" : ""}`} style={theater ? { borderColor: "var(--yalola-border)" } : undefined} data-testid={`comment-column-${post.id}`}>
        {embedded ? (
          <div className="relative px-4 pt-2 pb-2.5 border-b shrink-0" style={{ borderColor: "var(--yalola-border)" }} data-testid={`comment-sheet-header-${post.id}`}>
            <div className="mx-auto h-1 w-10 rounded-full mb-2" style={{ background: "var(--yalola-border)" }} aria-hidden />
            <div className="text-center font-display font-semibold text-sm" data-testid={`comment-sheet-title-${post.id}`}>
              {commentTotal === 1 ? t("comment.count_one", "1 commentaire") : t("comment.count", "{n} commentaires").replace("{n}", commentTotal)}
            </div>
            <button type="button" onClick={() => onOpenChange(false)} className="absolute right-3 top-3 h-8 w-8 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)]" aria-label={t("common.close", "Fermer")} data-testid={`comment-sheet-close-${post.id}`}>
              <X size={18} />
            </button>
          </div>
        ) : (
        <DialogHeader className={`px-4 py-3 border-b shrink-0 ${theater ? "[&_[data-dialog-close]]:top-[calc(0.75rem+var(--sa-top,0px))]" : ""}`} style={{ borderColor: "var(--yalola-border)", ...(theater ? { paddingTop: "calc(0.75rem + var(--sa-top, 0px))" } : null) }}>
          <DialogTitle className="text-center font-display text-base pr-6">{t("comment.post_by", "Publication de {name}").replace("{name}", post.author?.name || "")}</DialogTitle>
          <DialogDescription className="sr-only">{t("comment.title", "Commentaires")}</DialogDescription>
        </DialogHeader>
        )}

        <div className="flex-1 min-h-0 overflow-y-auto" data-testid={`comment-scroll-${post.id}`}>
          {!embedded && <PostLiveReactions post={post} />}
          {!embedded && <PostFull post={{ ...post, album: postMeta.album }} onComment={() => (user ? inputRef.current?.focus() : requireLogin("comment"))} onOpenPhoto={openPhoto} media={media} hideMedia={theater} focusReactions={focusReactions} />}

          <div className={`px-4 py-4 space-y-4 ${embedded ? "" : "border-t mt-2"}`} style={{ borderColor: "var(--yalola-border)" }} data-testid={`comment-list-${post.id}`}>
            {comments === null && (
              <div className="py-10 grid place-items-center"><Loader2 className="animate-spin" style={{ color: "var(--yalola-primary)" }} /></div>
            )}
            {comments !== null && topLevel.length === 0 && (
              <div className="py-10 text-center text-sm flex flex-col items-center gap-2" style={{ color: "var(--yalola-text-muted)" }} data-testid={`comment-empty-${post.id}`}>
                <MessageCircle size={28} className="opacity-40" />
                {t("comment.empty", "Aucun commentaire pour l'instant. Sois le premier !")}
              </div>
            )}
            {topLevel.map((c) => {
              let replies = repliesOf[c.id] || [];
              const expanded = !!expandedReplies[c.id];
              let bestId = null;
              if (c.pinned && replies.length > 0) {
                // Surface the most-appreciated replies under a pinned comment.
                replies = [...replies].sort((a, b) => reactTotal(b) - reactTotal(a));
                if (reactTotal(replies[0]) > 0) bestId = replies[0].id;
              }
              return (
                <div key={c.id}>
                  <CommentRow c={c} isReply={false} replyCount={replies.length} expanded={expanded} {...rowProps} />
                  {expanded && replies.map((r) => (<CommentRow key={r.id} c={r} isReply isBest={r.id === bestId} {...rowProps} />))}
                </div>
              );
            })}
          </div>
        </div>

        {typingNames.length > 0 && (
          <div className="px-4 pb-1 text-[12px] italic flex items-center gap-1.5 shrink-0" style={{ color: "var(--yalola-text-muted)" }} data-testid={`comment-typing-${post.id}`}>
            <span className="flex gap-0.5">
              <span className="h-1.5 w-1.5 rounded-full animate-bounce" style={{ background: "var(--yalola-primary)", animationDelay: "0ms" }} />
              <span className="h-1.5 w-1.5 rounded-full animate-bounce" style={{ background: "var(--yalola-primary)", animationDelay: "120ms" }} />
              <span className="h-1.5 w-1.5 rounded-full animate-bounce" style={{ background: "var(--yalola-primary)", animationDelay: "240ms" }} />
            </span>
            {typingLabel}
          </div>
        )}

        {!user && <GuestCta safeBottom={theater} testId={`guest-cta-${post.id}`} />}
        {user && (
          <div className="border-t px-4 py-3 shrink-0" style={{ borderColor: "var(--yalola-border)", ...(theater ? { paddingBottom: "calc(0.75rem + var(--sa-bottom, 0px))" } : null) }}>
            {replyTo && (
              <div className="flex items-center justify-between text-xs mb-2 px-1" style={{ color: "var(--yalola-text-muted)" }} data-testid={`comment-replying-${post.id}`}>
                <span>{t("comment.replying_to", "En réponse à {name}").replace("{name}", replyTo.name || "")}</span>
                <button onClick={() => { setReplyTo(null); setText(""); setMentions([]); }} className="hover:text-red-600" data-testid={`comment-cancel-reply-${post.id}`}><X size={14} /></button>
              </div>
            )}
            {/* Lot 598 — profil incomplet : le premier tap ouvre la feuille « Complète ton profil », pas le clavier. */}
            <div className="flex gap-2 items-center relative" {...profileGateProps(user)} data-testid={`comment-modal-bar-${post.id}`}>
              {mentionOpen && mentionResults.length > 0 && (
                <div className="absolute bottom-12 left-10 right-12 z-50 rounded-xl border bg-white shadow-lg overflow-hidden" style={{ borderColor: "var(--yalola-border)" }} data-testid={`comment-mention-list-${post.id}`}>
                  {mentionResults.map((u) => (
                    <button key={u.id} onClick={() => pickMention(u)} className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[color:var(--yalola-surface-alt)]" data-testid={`comment-mention-${u.id}`}>
                      <Avatar className="h-7 w-7"><AvatarImage src={avatarSrc(u)} /><AvatarFallback>{u.name?.[0]}</AvatarFallback></Avatar>
                      <span className="text-sm font-medium inline-flex items-center gap-1">{u.name} <VerifiedBadge user={u} size={11} /></span>
                    </button>
                  ))}
                </div>
              )}
              <Avatar className="h-8 w-8"><AvatarImage src={avatarSrc(user)} /><AvatarFallback>{user.name?.[0]}</AvatarFallback></Avatar>
              <div className="flex-1 flex items-center gap-1 rounded-full border bg-white pr-1" style={{ borderColor: "var(--yalola-border)" }}>
                <input
                  ref={inputRef}
                  value={text}
                  onChange={(e) => onChangeText(e.target.value)}
                  onBlur={stopTyping}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder={replyTo ? t("comment.write_reply", "Écris une réponse…") : t("comment.write", "Écris un commentaire…")}
                  className="flex-1 h-9 rounded-full px-3 text-base md:text-sm bg-transparent outline-none"
                  data-testid={`comment-modal-input-${post.id}`}
                />
                <Popover open={emojiOpen} onOpenChange={setEmojiOpen}>
                  <PopoverTrigger asChild>
                    <button type="button" className="h-8 w-8 grid place-items-center rounded-full hover:bg-[color:var(--yalola-surface-alt)]" {...keepFocusTap(() => setEmojiOpen((v) => !v), { withClick: false })} title={t("comment.add_emoji", "Ajouter un emoji")} data-testid={`comment-emoji-btn-${post.id}`}>
                      <Smile size={18} style={{ color: "var(--yalola-primary)" }} />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-60 p-2 z-[110]" align="end" side="top" collisionPadding={8} onOpenAutoFocus={(e) => e.preventDefault()} onCloseAutoFocus={(e) => e.preventDefault()} data-testid={`comment-emoji-panel-${post.id}`}>
                    <EmojiTabsGrid cols={6} cell="h-8 w-8 text-[22px]" onPick={insertEmoji} tapProps={(fn) => keepFocusTap(fn)} gridTestId={`comment-emoji-grid-${post.id}`} itemTestId={(e) => `comment-emoji-${e}-${post.id}`} />
                  </PopoverContent>
                </Popover>
              </div>
              <Button size="sm" className="rounded-full pill-primary" {...keepFocusTap(send)} disabled={sending || !text.trim()} data-testid={`comment-modal-send-${post.id}`}>
                {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </Button>
            </div>
          </div>
        )}
        </div>
  );
  const extras = (
    <>
      <BlockUserDialog open={!!blockTarget} onOpenChange={(v) => { if (!v) setBlockTarget(null); }} user={blockTarget} testidPrefix="comment-block" />
      <ReportDialog
        open={!!reportComment}
        onOpenChange={(v) => { if (!v) setReportComment(null); }}
        onReport={doReportComment}
        reporting={reporting}
        testidPrefix="comment-report"
        title={t("comment.report_title", "Signaler ce commentaire")}
      />
      <ReactorsDialog
        open={!!reactorsCommentId}
        onOpenChange={(v) => { if (!v) setReactorsCommentId(null); }}
        endpoint={reactorsCommentId ? `/comments/${reactorsCommentId}/reactions` : ""}
        testId="comment-reactors-dialog"
      />
      {lightbox !== null && zoomImages.length > 0 && (
        <Lightbox
          images={zoomImages}
          index={Math.min(lightbox, zoomImages.length - 1)}
          onIndex={(fn) => setLightbox((prev) => (typeof fn === "function" ? fn(prev) : fn))}
          onClose={closePhoto}
          author={<LightboxAuthor post={post} />}
          caption={post.content || ""}
          shareLink={`/post/${post.id}`}
          downloadUrl={zoomImages[Math.min(lightbox, zoomImages.length - 1)] || null}
          maxChars={photoCaptionChars(config)}
          footer={media && zoomAlbumIndex !== null ? (
            <>
              {(postMeta.album?.[zoomAlbumIndex]?.caption || media.canEditCaption) && (
                <div className="px-3 py-1.5 rounded-lg bg-black/50 backdrop-blur w-full" data-testid="lightbox-caption">
                  <EditableCaption postId={post.id} mediaIndex={zoomAlbumIndex} caption={postMeta.album?.[zoomAlbumIndex]?.caption || ""} canEdit={media.canEditCaption} dark onSaved={(c) => media.updateCaption(zoomAlbumIndex, c)} testId="lightbox-caption-editor" />
                </div>
              )}
              <div className="rounded-2xl bg-black/50 backdrop-blur px-1.5 border border-white/15">
                <MediaReactionBar key={zoomAlbumIndex} postId={post.id} mediaIndex={zoomAlbumIndex} initial={media.mrFor(zoomAlbumIndex)} dark onChange={(c, my) => media.onMediaReact(zoomAlbumIndex, c, my)} testId="lightbox-reactions" />
              </div>
            </>
          ) : null}
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <div className="flex flex-col h-full min-h-0 bg-white" data-testid={`comment-sheet-${post.id}`}>
        {column}
        {extras}
      </div>
    );
  }
  return (
    <>
      {theater && <TheaterStage post={{ ...post, album: postMeta.album }} media={theaterMedia} mediaCtl={media} onOpenPhoto={openPhoto} photoIndex={stageIndex} onPhotoIndex={setStageIndex} videoNav={videoNav} />}
      {column}
      {extras}
    </>
  );
};

// Dialog shell kept mounted while the body is re-keyed per post: stepping to the next video
// of the feed (‹ › / ← →) swaps stage + column without closing/reopening the theater.
export const CommentModal = (props) => {
  const { post, open, onOpenChange, embedded = false, initialPhoto = null } = props;
  const isMobile = useIsMobile();
  const guardRef = useRef(false); // true while a photo is zoomed in the Lightbox over this dialog
  if (embedded) return <CommentModalBody {...props} />;
  const kind = theaterKindFor(post, { initialPhoto, isMobile, embedded });
  const theater = !!kind;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Theater (photos, video / replay, YouTube, mixed album — ads included): ONE total-fullscreen
        // layout, black stage left + 460 px column right, same chrome as the photo viewer.
        className={theater
          ? "max-w-none w-screen h-[100dvh] !max-h-none !rounded-none border-0 p-0 sm:p-0 [--dlg-pad:0px] sm:[--dlg-pad:0px] gap-0 overflow-hidden !flex flex-row"
          : "max-w-[560px] p-0 [--dlg-pad:0px] sm:[--dlg-pad:0px] gap-0 overflow-hidden !flex flex-col"}
        data-theater={theater ? "1" : undefined}
        data-theater-kind={kind || undefined}
        // While a photo is zoomed (Lightbox portal outside this content) keep the modal open:
        // clicks in the lightbox and Escape must only close the zoom.
        onPointerDownOutside={(e) => { if (guardRef.current) e.preventDefault(); }}
        onInteractOutside={(e) => { if (guardRef.current) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (guardRef.current) e.preventDefault(); }}
        data-testid={`comment-modal-${post.id}`}
      >
        <CommentModalBody key={post.id} {...props} guardRef={guardRef} />
      </DialogContent>
    </Dialog>
  );
};

export default CommentModal;
